import { useState } from 'react';
import TaskCard from '@/Components/Query/TaskCard';
import TaskForm from '@/Components/Query/TaskForm';

export default function TasksTab({ query, tasks = [], users = [] }) {
    const [showForm, setShowForm] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [filter, setFilter] = useState('all');
    const [selectedTasks, setSelectedTasks] = useState([]);

    const handleEdit = (task) => {
        setEditingTask(task);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingTask(null);
    };

    const handleSelectTask = (taskId) => {
        setSelectedTasks(prev =>
            prev.includes(taskId)
                ? prev.filter(id => id !== taskId)
                : [...prev, taskId]
        );
    };

    const filteredTasks = tasks.filter(task => {
        if (filter === 'all') return true;
        if (filter === 'pending') return task.status === 'pending' || task.status === 'in_progress';
        if (filter === 'completed') return task.status === 'completed';
        if (filter === 'overdue') {
            return task.due_date &&
                task.status !== 'completed' &&
                task.status !== 'cancelled' &&
                new Date(task.due_date) < new Date(new Date().setHours(0, 0, 0, 0));
        }
        if (filter === 'today') {
            return task.due_date &&
                new Date(task.due_date).toDateString() === new Date().toDateString();
        }
        return true;
    });

    const pendingCount = tasks.filter(t => t.status === 'pending' || t.status === 'in_progress').length;
    const completedCount = tasks.filter(t => t.status === 'completed').length;
    const overdueCount = tasks.filter(t =>
        t.due_date &&
        t.status !== 'completed' &&
        t.status !== 'cancelled' &&
        new Date(t.due_date) < new Date(new Date().setHours(0, 0, 0, 0))
    ).length;
    const todayCount = tasks.filter(t =>
        t.due_date &&
        new Date(t.due_date).toDateString() === new Date().toDateString()
    ).length;

    return (
        <div className="qd-panel">
            <div className="qd-panel-header">
                <h3>Tasks & Follow-ups</h3>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => { setEditingTask(null); setShowForm(true); }}
                    >
                        <i className="bi bi-telephone me-1"></i>
                        Schedule Call
                    </button>
                    <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => { setEditingTask({ type: 'meeting' }); setShowForm(true); }}
                    >
                        <i className="bi bi-calendar-event me-1"></i>
                        Schedule Meeting
                    </button>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={() => { setEditingTask(null); setShowForm(true); }}
                    >
                        <i className="bi bi-plus-lg me-1"></i>
                        Add Task
                    </button>
                </div>
            </div>

            <div className="task-filters">
                <button
                    className={`task-filter-btn ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => setFilter('all')}
                >
                    All <span className="badge bg-secondary">{tasks.length}</span>
                </button>
                <button
                    className={`task-filter-btn ${filter === 'pending' ? 'active' : ''}`}
                    onClick={() => setFilter('pending')}
                >
                    Pending <span className="badge bg-warning text-dark">{pendingCount}</span>
                </button>
                <button
                    className={`task-filter-btn ${filter === 'overdue' ? 'active' : ''}`}
                    onClick={() => setFilter('overdue')}
                >
                    Overdue <span className="badge bg-danger">{overdueCount}</span>
                </button>
                <button
                    className={`task-filter-btn ${filter === 'today' ? 'active' : ''}`}
                    onClick={() => setFilter('today')}
                >
                    Due Today <span className="badge bg-info">{todayCount}</span>
                </button>
                <button
                    className={`task-filter-btn ${filter === 'completed' ? 'active' : ''}`}
                    onClick={() => setFilter('completed')}
                >
                    Completed <span className="badge bg-success">{completedCount}</span>
                </button>
            </div>

            <div className="qd-panel-body">
                {filteredTasks.length === 0 ? (
                    <div className="qd-empty">
                        <i className="bi bi-list-task"></i>
                        <h4>No Tasks Found</h4>
                        <p>
                            {filter === 'all'
                                ? 'Create tasks and follow-ups to track activities for this query.'
                                : `No ${filter} tasks for this query.`}
                        </p>
                        {filter === 'all' && (
                            <div className="qd-empty-actions">
                                <button
                                    className="btn btn-outline-primary me-2"
                                    onClick={() => { setEditingTask({ type: 'call' }); setShowForm(true); }}
                                >
                                    <i className="bi bi-telephone me-1"></i>
                                    Schedule Call
                                </button>
                                <button
                                    className="btn btn-outline-primary me-2"
                                    onClick={() => { setEditingTask({ type: 'meeting' }); setShowForm(true); }}
                                >
                                    <i className="bi bi-calendar-event me-1"></i>
                                    Schedule Meeting
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => { setEditingTask(null); setShowForm(true); }}
                                >
                                    <i className="bi bi-plus-lg me-1"></i>
                                    Add Task
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="task-list">
                        {filteredTasks.map(task => (
                            <TaskCard
                                key={task.id}
                                task={task}
                                queryId={query.id}
                                onEdit={handleEdit}
                                selectable={false}
                            />
                        ))}
                    </div>
                )}
            </div>

            {showForm && (
                <TaskForm
                    queryId={query.id}
                    task={editingTask}
                    users={users}
                    onClose={handleCloseForm}
                />
            )}

            <style>{`
                .task-filters {
                    display: flex;
                    gap: 0.5rem;
                    padding: 0.75rem 1.25rem;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                    overflow-x: auto;
                }

                .task-filter-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem 1rem;
                    background: #fff;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    font-size: 0.8125rem;
                    font-weight: 500;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.15s;
                    white-space: nowrap;
                }

                .task-filter-btn:hover {
                    border-color: #cbd5e1;
                    color: #334155;
                }

                .task-filter-btn.active {
                    background: #2563eb;
                    border-color: #2563eb;
                    color: #fff;
                }

                .task-filter-btn.active .badge {
                    background: rgba(255,255,255,0.2) !important;
                    color: #fff !important;
                }

                .task-list {
                    padding: 0.5rem 0;
                }
            `}</style>
        </div>
    );
}
